import React, { lazy } from "react";
import {
  AppstoreAddOutlined,
  DashboardOutlined,
  DollarOutlined,
  OrderedListOutlined,
  RiseOutlined,
  ScheduleOutlined,
  SettingOutlined,
  TagsOutlined,
  CarOutlined,
  StarOutlined,
  FundOutlined,
  UserSwitchOutlined,
  SoundOutlined,
  TeamOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { capitalize } from ".";

export const authService = "firebase";

// Helper function to handle import errors
const lazyWithRetry = (componentImport) => {
  return lazy(() => {
    return new Promise((resolve, reject) => {
      // First attempt
      componentImport()
        .then(resolve)
        .catch((error) => {
          // Retry once after a short delay
          setTimeout(() => {
            console.warn("Retrying lazy load after error:", error);
            componentImport().then(resolve).catch(reject);
          }, 500);
        });
    });
  });
};

/**
 * @typedef {Object} RouteConfig
 * @property {string} title - The route title (for menu)
 * @property {Function} icon - Icon component for the menu item
 * @property {Object} route - React Router configuration
 * @property {string} route.path - URL path
 * @property {boolean} [route.exact] - Whether path should match exactly
 * @property {string} layoutType - Layout wrapper type
 * @property {string} authType - Authentication requirement
 * @property {React.ComponentType} component - Page component
 * @property {boolean} [menuItem] - Whether to show in menu
 */

export const routes = [
  // Dashboard (home) route
  {
    title: (lang) => capitalize(lang("menu_dashboard")),
    icon: DashboardOutlined,
    route: {
      path: "/",
      exact: true,
    },
    layoutType: "dashboard",
    authType: "only-authenticated",
    roles: ["shop-owner", "shop-admin"],
    component: lazyWithRetry(() => import("../pages/Dashboard")),
    menuItem: true,
  },
  {
    title: (lang) => capitalize(lang("menu_announcements")),
    icon: SoundOutlined,
    route: {
      path: "/announcements",
      exact: true,
    },
    layoutType: "dashboard",
    authType: "only-authenticated",
    roles: ["shop-owner", "shop-admin"],
    component: lazyWithRetry(() => import("../pages/Announcements")),
    menuItem: true,
  },
  {
    title: (lang) => capitalize(lang("menu_staff")),
    icon: TeamOutlined,
    route: {
      path: "/staff",
      exact: true,
    },
    layoutType: "dashboard",
    authType: "only-authenticated",
    roles: ["shop-owner"],
    component: lazyWithRetry(() => import("../pages/Users")),
    menuItem: true,
  },
  {
    title: (lang) => capitalize(lang("menu_orders")),
    icon: ScheduleOutlined,
    route: {
      path: "/orders",
      exact: true,
    },
    layoutType: "dashboard",
    authType: "only-authenticated",
    roles: ["shop-owner", "shop-admin", "shop-chef"],
    component: lazyWithRetry(() => import("../pages/Orders")),
    menuItem: true,
  },
  {
    title: (lang) => capitalize(lang("menu_pos")),
    icon: TagsOutlined,
    route: {
      path: "/pos",
      exact: true,
    },
    layoutType: "dashboard",
    authType: "only-authenticated",
    roles: ["shop-owner", "shop-admin", "shop-pos-operator"],
    component: lazyWithRetry(() => import("../pages/Pos")),
    menuItem: true,
  },
  {
    title: () => "Tables",
    icon: TableOutlined,
    route: {
      path: "/tables",
      exact: true,
    },
    layoutType: "dashboard",
    authType: "only-authenticated",
    roles: ["shop-owner", "shop-admin", "shop-pos-operator", "shop-waiter"],
    component: lazyWithRetry(() => import("../pages/Tables")),
    menuItem: true,
  },
  {
    route: {
      path: "/items/add-item",
      exact: true,
    },
    layoutType: "dashboard",
    authType: "only-authenticated",
    roles: ["shop-owner", "shop-admin"],
    component: lazyWithRetry(() => import("../pages/AddItem")),
    menuItem: false,
  },
  {
    route: {
      path: "/items/add-deal",
      exact: true,
    },
    layoutType: "dashboard",
    authType: "only-authenticated",
    roles: ["shop-owner", "shop-admin"],
    component: lazyWithRetry(() => import("../pages/AddItem")),
    menuItem: false,
  },
  {
    title: (lang) => capitalize(lang("menu_items")),
    icon: OrderedListOutlined,
    route: {
      path: "/items",
      exact: true,
    },
    layoutType: "dashboard",
    authType: "only-authenticated",
    roles: ["shop-owner", "shop-admin"],
    component: lazyWithRetry(() => import("../pages/Items")),
    menuItem: true,
  },
  {
    route: {
      path: "/categories/add-new",
      exact: true,
    },
    layoutType: "dashboard",
    authType: "only-authenticated",
    roles: ["shop-owner", "shop-admin"],
    component: lazyWithRetry(() => import("../pages/Categories/addNew")),
    menuItem: false,
  },
  {
    title: (lang) => capitalize(lang("menu_categories")),
    icon: AppstoreAddOutlined,
    route: {
      path: "/categories",
      exact: true,
    },
    layoutType: "dashboard",
    authType: "only-authenticated",
    roles: ["shop-owner", "shop-admin"],
    component: lazyWithRetry(() => import("../pages/Categories")),
    menuItem: true,
  },
  {
    route: {
      path: "/promotions/add-new",
      exact: true,
    },
    layoutType: "dashboard",
    authType: "only-authenticated",
    roles: ["shop-owner", "shop-admin"],
    component: lazyWithRetry(() => import("../pages/Promotions/addNew")),
    menuItem: false,
  },
  {
    title: (lang) => capitalize(lang("menu_promotions")),
    icon: RiseOutlined,
    route: {
      path: "/promotions",
      exact: true,
    },
    layoutType: "dashboard",
    authType: "only-authenticated",
    roles: ["shop-owner", "shop-admin"],
    component: lazyWithRetry(() => import("../pages/Promotions")),
    menuItem: true,
  },
  {
    title: (lang) => capitalize(lang("menu_transactions")),
    icon: DollarOutlined,
    route: {
      path: "/transactions",
      exact: true,
    },
    layoutType: "dashboard",
    authType: "only-authenticated",
    roles: ["shop-owner", "shop-admin"],
    component: lazyWithRetry(() => import("../pages/Transactions")),
    menuItem: true,
  },
  {
    title: (lang) => capitalize(lang("menu_riders")),
    icon: CarOutlined,
    route: {
      path: "/riders",
      exact: true,
    },
    layoutType: "dashboard",
    authType: "only-authenticated",
    roles: ["shop-owner", "shop-admin"],
    component: lazyWithRetry(() => import("../pages/Drivers")),
    menuItem: true,
  },
  {
    route: {
      path: "/riders/add-rider",
      exact: true,
    },
    layoutType: "dashboard",
    authType: "only-authenticated",
    roles: ["shop-owner", "shop-admin"],
    component: lazyWithRetry(() => import("../pages/AddDriver")),
    menuItem: false,
  },
  {
    title: (lang) => capitalize(lang("menu_customers")),
    icon: UserSwitchOutlined,
    route: {
      path: "/customers",
      exact: true,
    },
    layoutType: "dashboard",
    authType: "only-authenticated",
    roles: ["shop-owner"],
    component: lazyWithRetry(() => import("../pages/Customers")),
    menuItem: true,
  },
  {
    title: (lang) => capitalize(lang("menu_reviews")),
    icon: StarOutlined,
    route: {
      path: "/reviews",
      exact: true,
    },
    layoutType: "dashboard",
    authType: "only-authenticated",
    roles: ["shop-owner", "shop-admin"],
    component: lazyWithRetry(() => import("../pages/Reviews")),
    menuItem: true,
  },
  {
    title: (lang) => capitalize(lang("menu_settings")),
    icon: SettingOutlined,
    route: {
      path: "/settings",
      exact: true,
    },
    layoutType: "dashboard",
    authType: "only-authenticated",
    roles: ["shop-owner"],
    component: lazyWithRetry(() => import("../pages/Settings")),
    menuItem: true,
  },

  {
    title: (lang) => capitalize(lang("menu_refund_requests")),
    icon: FundOutlined,
    route: {
      path: "/refund-requests",
      exact: true,
    },
    layoutType: "dashboard",
    authType: "only-authenticated",
    roles: ["shop-owner", "shop-admin"],
    component: lazyWithRetry(() => import("../pages/RefundRequest")),
    menuItem: true,
  },

  {
    route: {
      path: "/login",
    },
    layoutType: "auth",
    authType: "only-unauthenticated",
    component: lazyWithRetry(() => import("../pages/Login")),
  },
  {
    route: {
      path: "/signup",
    },
    layoutType: "auth",
    authType: "only-unauthenticated",
    component: lazyWithRetry(() => import("../pages/Register/Signup")),
  },
  {
    route: {
      path: "/reset-password",
    },
    layoutType: "auth",
    authType: "only-unauthenticated",
    component: lazyWithRetry(() => import("../pages/Login/ResetPassword")),
  },
  {
    route: {
      path: "/register/thankyou",
    },
    layoutType: "auth",
    component: lazyWithRetry(() => import("../pages/Register/Thankyou")),
  },
  {
    route: {
      path: "/register",
    },
    layoutType: "auth",
    component: lazyWithRetry(() => import("../pages/Register")),
  },

  {
    route: {
      path: "*",
    },
    component: lazyWithRetry(() => import("../pages/404")),
  },
];
